// Scoring (spec §10): stock baseline per craft, efficiency index, credits.
import type { CraftSpec, EngineSpec, Baseline, Domain } from '../types';
import { resolveBuild } from '../physics/resolve';
import { predictRange } from '../physics/predict';

const baselines = new Map<string, Baseline>();

/** Stock build at default cruise in still air; computed once per craft/engine pair and cached. */
export function computeBaseline(craft: CraftSpec, engine: EngineSpec): Baseline {
  const key = `${craft.id}:${engine.id}`;
  const cached = baselines.get(key);
  if (cached) return cached;
  const r = predictRange(resolveBuild(craft, engine, []), craft.defaultCruise);
  const b: Baseline = { rangeNm: r.rangeNm, efficiency: r.efficiency };
  baselines.set(key, b);
  return b;
}

export function clearBaselineCache(): void {
  baselines.clear();
}

/** nm per kg of fuel (air) or nm per tonne (sea). */
export function efficiencyFor(domain: Domain, distanceNm: number, fuelUsedKg: number): number {
  if (fuelUsedKg <= 0) return 0;
  return domain === 'air' ? distanceNm / fuelUsedKg : (distanceNm * 1000) / fuelUsedKg;
}

export function efficiencyIndexFor(efficiency: number, baseline: Baseline): number {
  return baseline.efficiency > 0 ? efficiency / baseline.efficiency : 0;
}

/** round(distance × 600 / baseline range) + round(200 × max(0, index − 1)). A stock run earns about 600. */
export function creditsFor(distanceNm: number, efficiencyIndex: number, baselineRangeNm: number): number {
  const distancePart = baselineRangeNm > 0 ? Math.round((distanceNm * 600) / baselineRangeNm) : 0;
  return distancePart + Math.round(200 * Math.max(0, efficiencyIndex - 1));
}
